import { useState } from "react";
import { ChevronDown, Download, FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";

interface ExportMenuProps {
  onExcel: () => Promise<void>;
  onPdf: () => Promise<void>;
  disabled?: boolean;
}

/** "Export" dropdown with Excel and PDF options; shows progress and reports failures. */
const ExportMenu = ({ onExcel, onPdf, disabled }: ExportMenuProps) => {
  const { toast } = useToast();
  const [busy, setBusy] = useState<"excel" | "pdf" | null>(null);

  const run = async (kind: "excel" | "pdf", task: () => Promise<void>) => {
    setBusy(kind);
    try {
      await task();
      toast({ title: kind === "excel" ? "Excel file downloaded" : "PDF downloaded" });
    } catch {
      toast({ title: "Export failed", description: "Could not create the file. Try again.", variant: "destructive" });
    } finally {
      setBusy(null);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" disabled={disabled || busy !== null} className="gap-2">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          Export
          <ChevronDown className="h-3.5 w-3.5 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" collisionPadding={8} className="w-44">
        <DropdownMenuItem className="cursor-pointer gap-2" onClick={() => run("excel", onExcel)}>
          <FileSpreadsheet className="h-4 w-4 text-success" /> Excel (.xlsx)
        </DropdownMenuItem>
        <DropdownMenuItem className="cursor-pointer gap-2" onClick={() => run("pdf", onPdf)}>
          <FileText className="h-4 w-4 text-destructive" /> PDF
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ExportMenu;
